'use client';

import { FC, PropsWithChildren, useEffect, useRef } from 'react';

import { useBffClient } from '@abg/web-core/bff/data/client-provider';
import { SessionInitializationManager } from '@abg/web-core/bff/data/session-initialization-manager';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useRememberedLoginJourney } from '../../hooks';
import { getUserSession, refreshUserSession } from '../../shared/utils';
import { userStoreSelector } from '../../store';
import { cbpStore } from '../../store/cbp/cbp.store';
import { UserRole } from '../../store/user';

const TOKEN_EXPIRATION_TIME = 15 * 60 * 1000; // 15 minutes

export const SessionInitialization: FC<PropsWithChildren> = ({ children }) => {
  const bffClient = useBffClient();
  const { locale, country } = usePageContext();
  const userSession = userStoreSelector.use.userSession();
  const setUserSession = userStoreSelector.use.setUserSession();
  const getState = userStoreSelector.use.getState();

  const isJourneyCalled = useRef(false);

  const { startRememberedLoginJourney } = useRememberedLoginJourney();

  useEffect(() => {
    if (userSession) {
      // Session already present in memory (cookie is set); unblock any gated
      // requests immediately so they are not held until the client-side timeout.
      SessionInitializationManager.markInitialized();
      return;
    }

    const initializeSession = async () => {
      try {
        const { loginData } = getState();
        let session = await getUserSession({
          bffClient,
          params: { locale, country },
        });

        const isGuest =
          session.roles.length === 1 && session.roles.includes(UserRole.GUEST);
        const isSignup = session.roles.includes(UserRole.SIGNUP);
        const isRememberMeTokenValid =
          !!loginData?.remember_me_token_expire_at &&
          loginData.remember_me_token_expire_at > Date.now();

        const currentTime = Date.now();
        const timeTillExpiration = session.expiresAt - currentTime;

        if (isGuest && isRememberMeTokenValid && !isJourneyCalled.current) {
          isJourneyCalled.current = true; // Prevent multiple journey calls in dev mode
          await startRememberedLoginJourney();
          return;
        }

        // skip session refresh for signup role without refresh token, ARGWE-14527, ARGWE-26512
        const shouldSkipRefresh = isSignup && !session.refreshToken;

        // refresh session if token is expired
        if (!shouldSkipRefresh && timeTillExpiration < TOKEN_EXPIRATION_TIME) {
          try {
            session = await refreshUserSession(bffClient);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error refreshing session:', error);
          }
        }

        // Set user session in store
        setUserSession(session);

        // Check for cbpInfo and apply to CBP store
        if (session.cbpInfo?.parentSegment) {
          const { awd, memberNumber } = session.cbpInfo.parentSegment;
          const currentCbpState = cbpStore.getState();

          // Update CBP store with segment data including memberNumber
          cbpStore.getState().updateCbpData({
            segment: {
              ...currentCbpState.segment,
              awd,
              memberNumber,
            },
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error initializing session:', error);
      }
    };

    const manager = new SessionInitializationManager([initializeSession]);

    void manager.initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bffClient, country, locale, userSession, setUserSession, getState]);

  return <>{children}</>;
};
